import environment from 'utils/environment';
import notificationMessageList from 'utils/notificationMessageList';
import EventBus from 'utils/eventBus';
import Route from 'enum/route.enum';
import { redirectTo } from 'components/common/navigation/redirect';
import RemoteConfiguration from 'remoteConfiguration/remoteConfiguration';
import { isLoggedInDevice } from 'utils/isLoggedIn';

// Configuration constants for heartbeat and reconnection
const HEARTBEAT_INTERVAL = 600000; // 10 minutes (also idle threshold for ping)
const HEARTBEAT_JITTER = 30000; // ±30 seconds random jitter
const PONG_TIMEOUT = 15000; // 15 seconds to wait for pong response

class WebSocketService {
  static instance = null;
  callbacks = {};
  timeInterval = 0;

  static getInstance() {
    if (!WebSocketService.instance) {
      WebSocketService.instance = new WebSocketService();
    }
    return WebSocketService.instance;
  }

  constructor() {
    this.socketRef = null;
    this.heartbeatInterval = null;
    this.pongTimeout = null;
    this.lastMessageTime = Date.now();
  }

  connect(device_id) {
    if (this.socketRef && this.socketRef.readyState === 1) {
      return;
    }

    this.socketRef = new WebSocket(environment.getSocketUrl());

    this.socketRef.onopen = () => {
      console.log('WebSocket connected.');
      // Send handshake message with device_id
      this.webSocketHandShake(device_id);
      // Start heartbeat to monitor connection health
      this.startHeartbeat();
    };

    this.socketRef.onmessage = (e) => {
      this.socketNewMessage(e.data);
    };

    this.socketRef.onerror = (e) => {
      console.error('WebSocket error occurred:', e.message || e);
      // Force close to trigger onclose handler (which manages reconnection)
      this.disconnect();
    };

    this.socketRef.onclose = () => {
      console.log('WebSocket closed');
      // Clean up heartbeat and pong timeout
      this.stopHeartbeat();
      this.clearPongTimeout();
      // Publish event for UI
      EventBus.publish('webSocketConnection', '');
      // Handle reconnection logic
      this.handleConnectionClosed();
    };
  }
  /**
   * Start heartbeat mechanism to periodically check connection health
   * Sends ping at HEARTBEAT_INTERVAL with jitter to prevent thundering herd
   * Skips ping if message activity detected within MESSAGE_ACTIVITY_THRESHOLD
   */
  startHeartbeat() {
    // Clear any existing heartbeat
    this.stopHeartbeat();

    // Calculate interval with jitter: base ± random offset
    const getNextInterval = () => {
      const jitter = (Math.random() - 0.5) * HEARTBEAT_JITTER; // -15s to +15s
      return HEARTBEAT_INTERVAL + jitter;
    };

    const scheduleNextPing = () => {
      const nextInterval = getNextInterval();
      console.log('Next ping scheduled in ' + (nextInterval / 1000).toFixed(1) + 's');

      this.heartbeatInterval = setTimeout(() => {
        this.sendPing();
        scheduleNextPing(); // Reschedule with new jitter
      }, nextInterval);
    };

    scheduleNextPing();
    console.log('Heartbeat started (5min interval with ±30s jitter)');
  }

  /**
   * Stop the heartbeat mechanism
   */
  stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
      console.log('Heartbeat stopped');
    }
  }

  /**
   * Send ping message to server and wait for pong response
   * Skips ping if there's recent message activity (within HEARTBEAT_INTERVAL)
   * If pong doesn't arrive within PONG_TIMEOUT, close connection
   */
  sendPing() {
    if (!this.socketRef || this.socketRef.readyState !== 1) {
      console.warn(
        'Cannot send ping: socket not open (state: ' +
          (this.socketRef ? this.socketRef.readyState : 'null') +
          ')'
      );
      return;
    }

    // Check if there's been recent message activity
    const timeSinceLastMessage = Date.now() - this.lastMessageTime;
    if (timeSinceLastMessage < HEARTBEAT_INTERVAL) {
      console.log(
        'Skipping ping: recent activity detected ' +
          (timeSinceLastMessage / 1000).toFixed(1) +
          's ago'
      );
      return;
    }

    this.sendMessage({
      type: notificationMessageList.ping,
      timestamp: Date.now(),
    });
    console.log('Ping sent (idle for ' + (timeSinceLastMessage / 1000).toFixed(1) + 's)');

    // Set timeout for pong response
    this.clearPongTimeout(); // Clear any existing timeout
    this.pongTimeout = setTimeout(() => {
      console.error('Pong not received within ' + PONG_TIMEOUT / 1000 + 's—connection is broken');
      // Close socket to trigger reconnection
      this.disconnect();
    }, PONG_TIMEOUT);
  }

  /**
   * Clear pending pong timeout
   */
  clearPongTimeout() {
    if (this.pongTimeout) {
      clearTimeout(this.pongTimeout);
      this.pongTimeout = null;
    }
  }

  /**
   * Handle connection closed event
   * Determines if reconnection is needed based on login status
   */
  handleConnectionClosed() {
    const isLoggedIn = isLoggedInDevice();

    if (isLoggedIn) {
      console.log('Device is logged in, publishing ServerDown event and scheduling reconnection');
      EventBus.publish('ServerDown');
      RemoteConfiguration.reconnectionCountDown();
    } else if (
      window.location.pathname !== Route.route.NOTFOUND &&
      window.location.pathname !== Route.route.DEVICEIPNOTFOUND
    ) {
      console.log('Device not logged in, redirecting to NOTFOUND');
      redirectTo(Route.route.NOTFOUND);
    }
  }

  disconnect() {
    // Close socket—onclose handler will clean up timers
    if (this.socketRef && this.socketRef.readyState !== 3) {
      this.socketRef.close();
    }
  }

  // WebSocket handshake method (same as Android implementation)
  webSocketHandShake(device_id) {
    console.log('Sending handshake message...');
    const handshakeMessage = {
      type: 'READY',
      device_id: device_id,
    };
    console.log('Handshake message sent:', handshakeMessage);
    this.sendMessage(handshakeMessage);
  }

  socketNewMessage(data) {
    console.log(data);
    const parsedData = JSON.parse(data);
    console.log(parsedData);

    // Update last message time for activity tracking
    this.lastMessageTime = Date.now();

    // Handle ping/pong heartbeat messages
    // we do not need to ackknowledge ping pong as no notification is created for them
    if (parsedData.code === notificationMessageList.pong) {
      console.log('Pong received—connection is healthy');
      this.clearPongTimeout(); // Cancel the timeout
      return; // Don't process further
    }

    if (parsedData.code === notificationMessageList.ping) {
      console.log('Ping received from server, sending pong');
      // do not send pong to server - we are just taking ping from server as one way message
      // this.sendMessage({ type: 'pong', timestamp: parsedData.timestamp });
      return; // Don't process further
    }

    // Process application messages
    if (parsedData.code && this.callbacks[parsedData.code]) {
      this.callbacks[parsedData.code](data);
    }
  }

  /**
   * Register callback for all notification message types
   * Excludes ping/pong which are handled separately in socketNewMessage
   */
  addCallbacks(messagesCallback) {
    // Register callback for all message types except ping/pong
    Object.entries(notificationMessageList).forEach(([key, code]) => {
      if (code !== notificationMessageList.ping && code !== notificationMessageList.pong) {
        this.callbacks[code] = messagesCallback;
      }
    });
  }

  sendMessage(data) {
    try {
      console.log(data);
      this.socketRef.send(JSON.stringify(data));
    } catch (err) {
      console.log(err.message);
      this.disconnect();
    }
  }

  state() {
    return this.socketRef ? this.socketRef.readyState : 0;
  }
}

const WebSocketInstance = WebSocketService.getInstance();

export default WebSocketInstance;
